import * as React from "react";

import styles from "./ModalProvider.module.scss";

type ContextState = {
  actions: {
    showModal(children: React.ReactElement): void;
    closeModal(): void;
    setCloseGuard(guard: (() => boolean) | null): void;
    requestCloseModal(): void;
  };
};

export const ModalContext = React.createContext<ContextState>({
  actions: {
    showModal: (_children: React.ReactElement) => undefined,
    closeModal: () => undefined,
    setCloseGuard: (_guard: (() => boolean) | null) => undefined,
    requestCloseModal: () => undefined,
  },
});

export default (props: { children: React.ReactElement }) => {
  const [state, setState] = React.useState<React.ReactElement | null>(null);
  const closeGuardRef = React.useRef<(() => boolean) | null>(null);
  const currentRef = React.createRef<HTMLDivElement>();
  const showModal = (children: React.ReactElement) => {
    closeGuardRef.current = null;
    setState(children);
  };
  const closeModal = () => {
    closeGuardRef.current = null;
    setState(null);
    return;
  };
  const setCloseGuard = (guard: (() => boolean) | null) => {
    closeGuardRef.current = guard;
  };
  const requestCloseModal = React.useCallback(() => {
    if (closeGuardRef.current && !closeGuardRef.current()) {
      return;
    }
    closeGuardRef.current = null;
    setState(null);
  }, []);
  React.useEffect(() => {
    document.addEventListener("keydown", ev => {
      ev.key === "Escape" && requestCloseModal();
    });
  }, [requestCloseModal]);

  const mouseDownOnBackdropRef = React.useRef(false);
  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      mouseDownOnBackdropRef.current = e.target === currentRef.current;
    },
    [currentRef],
  );
  const handleBlur = React.useCallback(
    (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      if (mouseDownOnBackdropRef.current && e.target === currentRef.current) {
        requestCloseModal();
      }
      mouseDownOnBackdropRef.current = false;
    },
    [currentRef, requestCloseModal],
  );
  return (
    <ModalContext.Provider
      value={{
        actions: {
          showModal,
          closeModal,
          setCloseGuard,
          requestCloseModal,
        },
      }}
    >
      {props.children}
      {state && (
        <div
          ref={currentRef}
          onMouseDown={handleMouseDown}
          onClick={handleBlur}
          className={styles.topModal}
        >
          <div className={styles.modal}>{state}</div>
        </div>
      )}
    </ModalContext.Provider>
  );
};
